import { fetchWithTimeout } from './deco-http.js';
const PACK_RULES_ENDPOINTS = [
  'https://api.decorental.com.ar/v1/packs/rules',
  'https://api.decorental.com.ar/v1/packs/rules/',
];
const PACK_RULES_STORAGE_KEY = 'decorental.packRules.v2';
const PACK_RULES_STORAGE_TTL_MS = 5 * 60 * 1000;
export const CART_ORDER_PROPERTY = '_decorental_cart_order';

export function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

export function normalizeFurnitureGroupKey(value) {
  const normalized = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' y ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  if (/^mesas? comedor\b/.test(normalized)) {
    return 'mesa comedor';
  }

  if (/^mesas? de luz\b/.test(normalized)) {
    return 'mesa de luz';
  }

  if (/^veladores?\b/.test(normalized)) {
    return 'velador';
  }

  return normalized;
}

export function normalizeSku(value) {
  return String(value || '')
    .trim()
    .toUpperCase();
}

function parseAllowedQuantities(rule) {
  if (!Array.isArray(rule?.allowed_quantities)) return [];

  const allowedSet = new Set();
  rule.allowed_quantities.forEach((quantity) => {
    const parsed = Number(quantity);
    if (Number.isInteger(parsed) && parsed > 0) {
      allowedSet.add(parsed);
    }
  });

  return Array.from(allowedSet).sort((a, b) => a - b);
}

function formatAllowedQuantities(quantities) {
  if (!Array.isArray(quantities) || quantities.length === 0) return '';
  if (quantities.length === 1) return `${quantities[0]}`;

  if (quantities.length === 2) {
    return `${quantities[0]} o ${quantities[1]}`;
  }

  return `${quantities.slice(0, -1).join(', ')} o ${quantities[quantities.length - 1]}`;
}

export function resolveProductAvailability(product) {
  if (typeof product?.system_available === 'boolean') {
    return {
      available: product.system_available,
      source: 'system',
    };
  }

  return {
    available: product?.available !== false,
    source: String(product?.availability_source || 'legacy').trim() || 'legacy',
  };
}

export function buildPackRulesConfig(data) {
  const rulesByMueble = new Map();
  const typeLimitsByGroup = new Map();
  const furnitureBySku = new Map();
  const productsBySku = new Map();
  const productsByMueble = new Map();

  (data.rules || []).forEach((rule) => {
    const muebleKey = normalizeText(rule.mueble);
    if (!muebleKey) return;

    const allowedQuantities = parseAllowedQuantities(rule);

    rulesByMueble.set(muebleKey, {
      mueble: rule.mueble,
      allowZero: rule.allow_zero !== false,
      allowedQuantities,
    });
  });

  (data.type_limits || []).forEach((limit) => {
    const groupKey = normalizeFurnitureGroupKey(limit.group_key);
    const maxTypes = Number(limit.max_types);
    if (!groupKey || !Number.isInteger(maxTypes) || maxTypes <= 0) return;
    typeLimitsByGroup.set(groupKey, {
      label: String(limit.label || limit.group_key || groupKey).trim(),
      maxTypes,
    });
  });

  (data.products || []).forEach((product) => {
    const skuKey = normalizeSku(product.sku);
    const muebleKey = normalizeText(product.mueble);
    if (!skuKey || !muebleKey) return;
    const availability = resolveProductAvailability(product);

    const normalizedProduct = {
      sku: skuKey,
      nombre: String(product.nombre || skuKey).trim(),
      mueble: String(product.mueble || '').trim(),
      muebleKey,
      available: availability.available,
      availabilitySource: availability.source,
      publicationStatus: String(product.publication_status || '').trim(),
      stockAvailable: typeof product.stock_available === 'boolean' ? product.stock_available : null,
      shopifyAvailable: typeof product.shopify_available === 'boolean' ? product.shopify_available : null,
      imageUrl: String(product.image_url || '').trim(),
      productUrl: String(product.product_url || '').trim(),
      handle: String(product.handle || '').trim(),
    };

    furnitureBySku.set(skuKey, muebleKey);
    productsBySku.set(skuKey, normalizedProduct);
    if (!productsByMueble.has(muebleKey)) {
      productsByMueble.set(muebleKey, []);
    }
    productsByMueble.get(muebleKey).push(normalizedProduct);
  });

  productsByMueble.forEach((products) => {
    products.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }));
  });

  return {
    rulesByMueble,
    typeLimitsByGroup,
    furnitureBySku,
    productsBySku,
    productsByMueble,
    products: Array.from(productsBySku.values()),
  };
}

function readCachedPackRules() {
  try {
    const rawValue = sessionStorage.getItem(PACK_RULES_STORAGE_KEY);
    if (!rawValue) return null;

    const parsed = JSON.parse(rawValue);
    if (!parsed?.updatedAt || !parsed?.data) return null;
    if (Date.now() - parsed.updatedAt > PACK_RULES_STORAGE_TTL_MS) return null;

    return buildPackRulesConfig(parsed.data);
  } catch (error) {
    return null;
  }
}

function writeCachedPackRules(data) {
  try {
    sessionStorage.setItem(
      PACK_RULES_STORAGE_KEY,
      JSON.stringify({
        updatedAt: Date.now(),
        data,
      })
    );
  } catch (error) {
    // ignore cache write errors
  }
}

let packRulesRequest = null;
export async function getPackRulesConfig({ forceRefresh = false } = {}) {
  if (packRulesRequest) return packRulesRequest;
  packRulesRequest = loadPackRulesConfig({ forceRefresh }).finally(() => { packRulesRequest = null; });
  return packRulesRequest;
}

async function loadPackRulesConfig({ forceRefresh = false } = {}) {
  if (!forceRefresh) {
    const cachedConfig = readCachedPackRules();
    if (cachedConfig) return cachedConfig;
  }

  let response = null;
  /** @type {number[]} */
  const failedStatuses = [];

  for (const endpoint of PACK_RULES_ENDPOINTS) {
    response = await fetchWithTimeout(endpoint, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    if (response.ok) {
      break;
    }

    failedStatuses.push(response.status);
    // Only legacy route discovery warrants a second request.
    if (![404, 405].includes(response.status)) break;
  }

  if (!response?.ok) {
    const statusLabel = failedStatuses.length > 0 ? ` (status: ${failedStatuses.join(', ')})` : '';
    throw new Error(`No se pudieron cargar las reglas de packs${statusLabel}.`);
  }

  const data = await response.json();
  if (
    !data?.ok ||
    !Array.isArray(data.rules) ||
    !Array.isArray(data.type_limits) ||
    !Array.isArray(data.products)
  ) {
    throw new Error('La respuesta de reglas de packs es inválida.');
  }

  writeCachedPackRules(data);
  return buildPackRulesConfig(data);
}

export async function fetchCartState() {
  const provider = globalThis.DecoSystemSelection?.fetchCartState;
  if (typeof provider === 'function') {
    return provider();
  }

  const { fetchSystemSelectionCartState } = await import('./system-selection.js');
  return fetchSystemSelectionCartState();
}

export function mapSkuToFurniture(sku, config) {
  return config.furnitureBySku.get(normalizeSku(sku)) || null;
}

export function mapSkuToFurnitureGroup(sku, config) {
  const muebleKey = mapSkuToFurniture(sku, config);
  if (!muebleKey) return null;

  const rule = config.rulesByMueble.get(muebleKey);
  return normalizeFurnitureGroupKey(rule?.mueble || muebleKey);
}

export function getCartItemSelectionOrder(item, fallbackOrder = 0) {
  const rawOrder = item?.cartOrder ?? item?.properties?.[CART_ORDER_PROPERTY];
  const parsedOrder = Number(rawOrder);

  if (Number.isFinite(parsedOrder)) {
    return parsedOrder;
  }

  return fallbackOrder;
}

export function getSortedCartItemEntries(cartItems, config) {
  const items = Array.isArray(cartItems) ? cartItems : [];
  const groupFirstOrder = new Map();

  const entries = items.map((item, index) => {
    const selectionOrder = getCartItemSelectionOrder(item, items.length - 1 - index);
    const groupKey = mapSkuToFurnitureGroup(item?.sku, config) || `__line_${index}`;
    const groupOrder = groupFirstOrder.get(groupKey);

    if (!Number.isFinite(groupOrder) || selectionOrder < groupOrder) {
      groupFirstOrder.set(groupKey, selectionOrder);
    }

    return {
      item,
      line: index + 1,
      sourceIndex: index,
      selectionOrder,
      groupKey,
    };
  });

  return entries.sort((a, b) => {
    const groupOrderA = groupFirstOrder.get(a.groupKey) ?? a.selectionOrder;
    const groupOrderB = groupFirstOrder.get(b.groupKey) ?? b.selectionOrder;

    return (
      groupOrderA - groupOrderB ||
      a.selectionOrder - b.selectionOrder ||
      a.sourceIndex - b.sourceIndex
    );
  });
}

export function sortCartItemsBySelectionOrder(cartItems, config) {
  return getSortedCartItemEntries(cartItems, config).map((entry) => entry.item);
}

export function getFurnitureRuleForSku(sku, config) {
  const muebleKey = mapSkuToFurniture(sku, config);
  if (!muebleKey) return null;

  const rule = config.rulesByMueble.get(muebleKey);
  if (!rule) return null;

  return {
    muebleKey,
    rule,
  };
}

export function countByFurniture(cartItems, config) {
  const counts = new Map();

  cartItems.forEach((item) => {
    const muebleKey = mapSkuToFurniture(item.sku, config);
    if (!muebleKey) return;

    const currentQty = counts.get(muebleKey) || 0;
    counts.set(muebleKey, currentQty + Number(item.quantity || item.qty || 0));
  });

  return counts;
}

export function isQuantityAllowed(rule, quantity) {
  const parsedQuantity = Number(quantity);

  if (!Number.isInteger(parsedQuantity) || parsedQuantity < 0) {
    return false;
  }

  if (parsedQuantity === 0) {
    return Boolean(rule?.allowZero);
  }

  return Array.isArray(rule?.allowedQuantities) && rule.allowedQuantities.includes(parsedQuantity);
}

export function getMaxAllowedQuantity(rule) {
  const quantities = (rule?.allowedQuantities || [])
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value > 0)
    .sort((a, b) => a - b);

  return quantities.length > 0 ? quantities[quantities.length - 1] : null;
}


export function getAllowedTotals(rule) {
  const totals = new Set();

  if (rule?.allowZero) {
    totals.add(0);
  }

  (rule?.allowedQuantities || []).forEach((quantity) => {
    const parsed = Number(quantity);
    if (Number.isInteger(parsed) && parsed > 0) {
      totals.add(parsed);
    }
  });

  return Array.from(totals).sort((a, b) => a - b);
}

export function getNextAllowedQuantity(rule, quantity) {
  const parsed = Number(quantity);
  const totals = getAllowedTotals(rule);

  return totals.find((value) => value > parsed) ?? null;
}

export function getPreviousAllowedQuantity(rule, quantity) {
  const parsed = Number(quantity);
  const totals = getAllowedTotals(rule);

  for (let index = totals.length - 1; index >= 0; index -= 1) {
    if (totals[index] < parsed) {
      return totals[index];
    }
  }

  return null;
}


export function getPreviousPositiveAllowedQuantity(rule, quantity) {
  const parsed = Number(quantity);
  const totals = (rule?.allowedQuantities || [])
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value > 0)
    .sort((a, b) => a - b);

  for (let index = totals.length - 1; index >= 0; index -= 1) {
    if (totals[index] < parsed) {
      return totals[index];
    }
  }

  return null;
}

export function getSelectedSkuSet(cartItems) {
  const selectedSkus = new Set();

  (cartItems || []).forEach((item) => {
    const sku = normalizeSku(item?.sku);
    const qty = Number(item?.quantity || item?.qty || 0);
    if (sku && qty > 0) {
      selectedSkus.add(sku);
    }
  });

  return selectedSkus;
}

export function getMaxedFurnitureSet(cartItems, config) {
  const counts = countByFurniture(cartItems || [], config);
  const maxedFurniture = new Set();

  config.rulesByMueble.forEach((rule, muebleKey) => {
    const maxAllowed = getMaxAllowedQuantity(rule);
    if (maxAllowed === null) return;

    const selectedQty = counts.get(muebleKey) || 0;
    if (selectedQty >= maxAllowed) {
      maxedFurniture.add(muebleKey);
    }
  });

  return maxedFurniture;
}

export function getMaxedFurnitureGroupSet(cartItems, config) {
  const maxedFurniture = getMaxedFurnitureSet(cartItems, config);
  const maxedFurnitureGroups = new Set();

  maxedFurniture.forEach((muebleKey) => {
    const rule = config.rulesByMueble.get(muebleKey);
    const groupKey = normalizeFurnitureGroupKey(rule?.mueble || muebleKey);
    if (groupKey) {
      maxedFurnitureGroups.add(groupKey);
    }
  });

  return maxedFurnitureGroups;
}

export function countSelectedFurnitureGroups(cartItems, config) {
  const selectedGroups = new Set();

  (cartItems || []).forEach((item) => {
    const qty = Number(item?.quantity || item?.qty || 0);
    if (qty <= 0) return;

    const groupKey = mapSkuToFurnitureGroup(item?.sku, config);
    if (groupKey) {
      selectedGroups.add(groupKey);
    }
  });

  return selectedGroups;
}

export function countSelectedSkusByFurnitureGroup(cartItems, config) {
  const skusByGroup = new Map();

  (cartItems || []).forEach((item) => {
    const qty = Number(item?.quantity || item?.qty || 0);
    if (qty <= 0) return;

    const sku = normalizeSku(item?.sku);
    const groupKey = mapSkuToFurnitureGroup(sku, config);
    if (!sku || !groupKey) return;

    if (!skusByGroup.has(groupKey)) {
      skusByGroup.set(groupKey, new Set());
    }
    skusByGroup.get(groupKey).add(sku);
  });

  const counts = new Map();
  skusByGroup.forEach((skus, groupKey) => {
    counts.set(groupKey, skus.size);
  });
  return counts;
}

function getFurnitureGroupTypeLimit(groupKey, config) {
  return config?.typeLimitsByGroup?.get(normalizeFurnitureGroupKey(groupKey)) || null;
}

export function getFurnitureTypeLimitLabel(groupKey, config) {
  const limit = getFurnitureGroupTypeLimit(groupKey, config);
  return limit?.label || groupKey;
}

export function getMaxedFurnitureTypeGroupSet(cartItems, config) {
  const selectedTypesByGroup = countSelectedSkusByFurnitureGroup(cartItems, config);
  const maxedGroups = new Set();

  config.typeLimitsByGroup.forEach((limit, groupKey) => {
    const selectedTypes = selectedTypesByGroup.get(groupKey) || 0;
    if (selectedTypes >= limit.maxTypes) {
      maxedGroups.add(groupKey);
    }
  });

  return maxedGroups;
}

export function getPackAddDecision({ sku, requestedQuantity = 1, cartItems = [], config, canAdjustQuantity = false }) {
  const normalizedSku = normalizeSku(sku);
  const requestedQty = Number(requestedQuantity);

  if (!normalizedSku || !config) {
    return { allowed: true, quantity: requestedQty };
  }

  const product = config.productsBySku?.get(normalizedSku);
  if (product?.available === false) {
    return {
      allowed: false,
      reason: 'product_unavailable',
      message: 'Este producto no está disponible por el momento.',
    };
  }

  const selectedSkus = getSelectedSkuSet(cartItems);
  if (selectedSkus.has(normalizedSku)) {
    return {
      allowed: false,
      reason: 'product_already_selected',
      message: 'Este producto ya está en el carrito.',
    };
  }

  const furnitureGroupKey = mapSkuToFurnitureGroup(normalizedSku, config);
  const typeLimit = furnitureGroupKey ? getFurnitureGroupTypeLimit(furnitureGroupKey, config) : null;
  if (furnitureGroupKey && typeLimit) {
    const selectedTypes = countSelectedSkusByFurnitureGroup(cartItems, config).get(furnitureGroupKey) || 0;
    if (selectedTypes >= typeLimit.maxTypes) {
      return {
        allowed: false,
        reason: 'furniture_type_limit_reached',
        message: `${typeLimit.label}: podés elegir hasta ${typeLimit.maxTypes} tipos.`,
      };
    }
  }

  if (furnitureGroupKey && getMaxedFurnitureGroupSet(cartItems, config).has(furnitureGroupKey)) {
    return {
      allowed: false,
      reason: 'furniture_group_max_reached',
      message: 'Este tipo de producto ya alcanzó el máximo permitido.',
    };
  }

  const ruleEntry = getFurnitureRuleForSku(normalizedSku, config);
  if (!ruleEntry) {
    return { allowed: true, quantity: requestedQty };
  }

  const { muebleKey, rule } = ruleEntry;
  const currentQty = countByFurniture(cartItems, config).get(muebleKey) || 0;
  const safeRequestedQty = Number.isInteger(requestedQty) && requestedQty > 0 ? requestedQty : 1;
  const requestedTotal = currentQty + safeRequestedQty;

  if (isQuantityAllowed(rule, requestedTotal)) {
    return { allowed: true, quantity: safeRequestedQty };
  }

  if (canAdjustQuantity) {
    const nextAllowedTotal = getNextAllowedQuantity(rule, currentQty);
    if (nextAllowedTotal !== null && nextAllowedTotal > currentQty) {
      return {
        allowed: true,
        adjusted: true,
        quantity: nextAllowedTotal - currentQty,
        targetTotal: nextAllowedTotal,
      };
    }
  }

  const maxAllowed = getMaxAllowedQuantity(rule);
  const allowedText = formatAllowedQuantities(rule.allowedQuantities);
  const muebleLabel = rule.mueble || muebleKey;

  if (maxAllowed !== null && currentQty >= maxAllowed) {
    return {
      allowed: false,
      reason: 'furniture_max_reached',
      message: `${muebleLabel}: ya alcanzaste el máximo de ${maxAllowed} unidades.`,
    };
  }

  return {
    allowed: false,
    reason: 'quantity_not_allowed',
    message: allowedText
      ? `${muebleLabel}: total permitido ${allowedText} unidades.`
      : `${muebleLabel}: la cantidad elegida no está permitida.`,
  };
}

export function validatePackSelection(cartItems, config) {
  const counts = countByFurniture(cartItems, config);
  const selectedTypesByGroup = countSelectedSkusByFurnitureGroup(cartItems, config);
  const violations = [];

  config.rulesByMueble.forEach((rule, muebleKey) => {
    const selectedQty = counts.get(muebleKey) || 0;

    if (!isQuantityAllowed(rule, selectedQty)) {
      violations.push({
        type: selectedQty === 0 ? 'zero_not_allowed' : 'quantity_not_allowed',
        mueble: rule.mueble,
        selectedQty,
        allowZero: Boolean(rule.allowZero),
        allowedQuantities: rule.allowedQuantities,
      });
    }
  });

  config.typeLimitsByGroup.forEach((limit, groupKey) => {
    const selectedTypes = selectedTypesByGroup.get(groupKey) || 0;
    if (selectedTypes > limit.maxTypes) {
      violations.push({
        type: 'furniture_type_limit',
        mueble: limit.label,
        selectedTypes,
        maxTypes: limit.maxTypes,
      });
    }
  });

  return {
    valid: violations.length === 0,
    violations,
    counts,
  };
}

export function getViolationMessage(violation) {
  const allowedText = formatAllowedQuantities(violation?.allowedQuantities || []);
  const mueble = String(violation?.mueble || '').trim();
  const selectedQty = Number(violation?.selectedQty ?? violation?.actualQty);
  const actualText = Number.isFinite(selectedQty) ? ` Actual: ${selectedQty}.` : '';
  const prefix = mueble ? `${mueble}: ` : '';

  if (violation?.type === 'zero_not_allowed') {
    return `${prefix}permite 0 unidades.`;
  }

  if (violation?.type === 'quantity_not_allowed' && allowedText) {
    return `${prefix}total permitido ${allowedText} unidades.${actualText}`;
  }

  if (violation?.type === 'furniture_type_limit') {
    return `${prefix}podés elegir hasta ${violation.maxTypes} tipos. Actual: ${violation.selectedTypes}.`;
  }

  return 'La selección no cumple las reglas del pack.';
}
